import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

export type SegmentOption<T extends string> = {
    value: T
    label: ReactNode
    count?: number
}

interface SegmentedProps<T extends string> {
    options: SegmentOption<T>[]
    value: T
    onChange: (value: T) => void
    label: string
    size?: 'sm' | 'md'
    className?: string
    stretch?: boolean
}

/* A row of filter tabs. Scrolls sideways on narrow screens instead of wrapping. */
export const Segmented = <T extends string>({
    options,
    value,
    onChange,
    label,
    size = 'md',
    className,
    stretch = false
}: SegmentedProps<T>): ReactNode => {
    return (
        <div
            role='group'
            aria-label={label}
            className={cn('no-scrollbar flex gap-1 overflow-x-auto rounded-xl bg-slate-200/60 p-1', className)}
        >
            {options.map(option => {
                const active = option.value === value

                return (
                    <button
                        key={option.value}
                        type='button'
                        aria-pressed={active}
                        onClick={() => onChange(option.value)}
                        className={cn(
                            'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg px-3 font-semibold whitespace-nowrap transition-all duration-150 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                            size === 'sm' ? 'h-8 text-[13px]' : 'h-10 text-sm',
                            stretch && 'flex-1',
                            active ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
                        )}
                    >
                        {option.label}

                        {typeof option.count === 'number' && (
                            <span
                                className={cn(
                                    'min-w-5 rounded-full px-1.5 text-center text-[11px] leading-5 font-bold tabular-nums',
                                    active ? 'bg-primary text-primary-foreground' : 'bg-slate-300/70 text-slate-700'
                                )}
                            >
                                {option.count}
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )
}
